import React, { useState } from 'react';
import { useIndexedUserData, UserAnalyticsStats } from '../../hooks/useIndexedUserData';
import { IndexedUserData, UserAnalyticsSortField, SortDirection } from '../../types/indexedUser';
import {
  HiArrowPath,
  HiMagnifyingGlass,
  HiExclamationCircle,
  HiUsers,
  HiBell,
  HiEye,
  HiDevicePhoneMobile,
  HiSquares2X2,
  HiArrowDownTray,
  HiChevronUp,
  HiChevronDown,
  HiCalendarDays,
  HiCog6Tooth,
  HiFunnel,
  HiXMark,
  HiClock,
  HiChartBar,
  HiEnvelope,
} from 'react-icons/hi2';
import SendNotificationDialog from '../../components/SendNotificationDialog';
import SendEmailDialog from '../../components/SendEmailDialog';
import WearableDetailsDialog from '../../components/WearableDetailsDialog';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { TerraDevice } from '../../types/user';
import SearchableSelect from '../../components/common/SearchableSelect';

const UserAnalytics: React.FC = () => {
  const {
    users,
    loading,
    error,
    refreshData,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    filter,
    setFilter,
    filteredUsers,
    stats,
  } = useIndexedUserData();

  const [indexingInProgress, setIndexingInProgress] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState<{
    isOpen: boolean;
    targetUser: { id: string; name: string; surname: string; email: string } | null;
  }>({ isOpen: false, targetUser: null });

  const [emailDialog, setEmailDialog] = useState<{
    isOpen: boolean;
    targetUser: { id: string; name: string; surname: string; email: string } | null;
  }>({ isOpen: false, targetUser: null });

  const [wearableDialog, setWearableDialog] = useState<{
    isOpen: boolean;
    targetUser: { id: string; name: string; surname: string; email: string } | null;
    devices: TerraDevice[];
  }>({ isOpen: false, targetUser: null, devices: [] });
  const [removeDeadWearablesDialogOpen, setRemoveDeadWearablesDialogOpen] = useState(false);
  const [removeDeadWearablesRunning, setRemoveDeadWearablesRunning] = useState(false);
  const [removeDeadWearablesCurrentUser, setRemoveDeadWearablesCurrentUser] = useState<{
    id: string;
    name: string;
    index: number;
    total: number;
  } | null>(null);
  const [removeDeadWearablesSummary, setRemoveDeadWearablesSummary] = useState<{
    processedUsers: number;
    processedDevices: number;
    failedDevices: number;
  } | null>(null);
  const [removeDeadWearablesStatusByUser, setRemoveDeadWearablesStatusByUser] = useState<
    Record<
      string,
      {
        status: 'pending' | 'in_progress' | 'done';
        processedDevices: number;
        totalDevices: number;
        failedDevices: number;
      }
    >
  >({});

  const deadWearableCandidates = users.filter((user) => {
    const subscriptionType = (user.subscriptionType || 'default').toLowerCase();
    return (
      user.recentlyLogged === false &&
      subscriptionType === 'default' &&
      user.wearableDevicesCount > 0 &&
      user.wearableDevices.length > 0
    );
  });

  const handleSort = (field: UserAnalyticsSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Default to descending for numeric fields (show highest first)
      const numericFields: UserAnalyticsSortField[] = [
        'dayPostsCount',
        'postsCount',
        'aiChatsCount',
        'wearableDevicesCount',
        'trainingLogsCount',
        'activityLogsCount',
        'sessionsCount',
      ];
      setSortDirection(numericFields.includes(field) ? 'desc' : 'asc');
    }
  };

  const renderSortIcon = (field: UserAnalyticsSortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <HiChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <HiChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const handleRunIndexing = async () => {
    if (!window.confirm('This will re-index all user data. This may take a few minutes. Continue?')) {
      return;
    }

    setIndexingInProgress(true);
    try {
      const functions = getFunctions();
      // Increase timeout to 10 minutes for large datasets
      const runUserDataIndexing = httpsCallable(functions, 'runUserDataIndexing', { timeout: 600000 });
      const result = await runUserDataIndexing();
      const data = result.data as {
        success: boolean;
        totalUsers: number;
        indexedCount: number;
        errorCount: number;
        deletedCount: number;
        errors: Array<{ userId: string; error: string }>;
        message: string;
      };

      console.log('[INDEXING RESULT]', data);

      alert(`${data.message}\n\nRefreshing data...`);
      await refreshData();
      
      if (data.errorCount > 0 && data.errors && data.errors.length > 0) {
        console.error('[INDEXING ERRORS]', data.errors);
        const errorSamples = data.errors.slice(0, 5).map(e => `- ${e.userId}: ${e.error}`).join('\n');
        alert(`Indexing had ${data.errorCount} errors:\n\n${errorSamples}\n\nSee console for full details.`);
      }
    } catch (err) {
      console.error('Indexing failed:', err);
      alert(`Indexing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIndexingInProgress(false);
    }
  };

  const handleNotificationClick = (user: IndexedUserData) => {
    setNotificationDialog({
      isOpen: true,
      targetUser: {
        id: user.id,
        name: user.name,
        surname: user.surname,
        email: user.email,
      },
    });
  };

  const handleEmailClick = (user: IndexedUserData) => {
    setEmailDialog({
      isOpen: true,
      targetUser: {
        id: user.id,
        name: user.name,
        surname: user.surname,
        email: user.email,
      },
    });
  };

  const handleWearableClick = (user: IndexedUserData) => {
    const devices: TerraDevice[] = user.wearableDevices.map((d) => ({
      id: d.id,
      resource: d.resource,
      connectedAt: d.connectedAt,
      expired: d.expired,
    }));

    setWearableDialog({
      isOpen: true,
      targetUser: {
        id: user.id,
        name: user.name,
        surname: user.surname,
        email: user.email,
      },
      devices,
    });
  };

  const openRemoveDeadWearablesDialog = () => {
    const initialStatus: Record<
      string,
      {
        status: 'pending' | 'in_progress' | 'done';
        processedDevices: number;
        totalDevices: number;
        failedDevices: number;
      }
    > = {};

    deadWearableCandidates.forEach((user) => {
      initialStatus[user.id] = {
        status: 'pending',
        processedDevices: 0,
        totalDevices: user.wearableDevices.length,
        failedDevices: 0,
      };
    });

    setRemoveDeadWearablesStatusByUser(initialStatus);
    setRemoveDeadWearablesSummary(null);
    setRemoveDeadWearablesCurrentUser(null);
    setRemoveDeadWearablesDialogOpen(true);
  };

  const handleRemoveDeadWearables = async () => {
    if (deadWearableCandidates.length === 0 || removeDeadWearablesRunning) return;
    const USERS_PER_BATCH = 10;

    const confirmed = window.confirm(
      `This will remove wearables for ${deadWearableCandidates.length} user(s), processing up to ${USERS_PER_BATCH} users at a time. Continue?`
    );
    if (!confirmed) return;

    setRemoveDeadWearablesRunning(true);
    setRemoveDeadWearablesSummary(null);

    let processedUsers = 0;
    let processedDevices = 0;
    let failedDevices = 0;

    try {
      const functions = getFunctions();
      const adminDeauthDevice = httpsCallable(functions, 'adminDeauthDevice', {
        timeout: 600000,
      });

      const processSingleUser = async (user: IndexedUserData, userIndex: number) => {
        const devices = user.wearableDevices || [];

        setRemoveDeadWearablesCurrentUser({
          id: user.id,
          name: `${user.name} ${user.surname}`.trim() || user.email || user.id,
          index: userIndex + 1,
          total: deadWearableCandidates.length,
        });

        setRemoveDeadWearablesStatusByUser((prev) => ({
          ...prev,
          [user.id]: {
            ...(prev[user.id] || {
              status: 'pending',
              processedDevices: 0,
              totalDevices: devices.length,
              failedDevices: 0,
            }),
            status: 'in_progress',
          },
        }));

        let userProcessedDevices = 0;
        let userFailedDevices = 0;

        for (const device of devices) {
          try {
            await adminDeauthDevice({
              targetUserId: user.id,
              deviceId: device.id,
            });
          } catch (err) {
            userFailedDevices += 1;
            failedDevices += 1;
            console.error(`[REMOVE_DEAD_WEARABLES] Failed for ${user.id} device ${device.id}`, err);
          } finally {
            userProcessedDevices += 1;
            processedDevices += 1;
            const snapshotProcessed = userProcessedDevices;
            const snapshotFailed = userFailedDevices;
            setRemoveDeadWearablesStatusByUser((prev) => ({
              ...prev,
              [user.id]: {
                ...(prev[user.id] || {
                  status: 'in_progress',
                  processedDevices: 0,
                  totalDevices: devices.length,
                  failedDevices: 0,
                }),
                status: 'in_progress',
                processedDevices: snapshotProcessed,
                totalDevices: devices.length,
                failedDevices: snapshotFailed,
              },
            }));
          }
        }

        processedUsers += 1;
        setRemoveDeadWearablesStatusByUser((prev) => ({
          ...prev,
          [user.id]: {
            ...(prev[user.id] || {
              status: 'in_progress',
              processedDevices: userProcessedDevices,
              totalDevices: devices.length,
              failedDevices: userFailedDevices,
            }),
            status: 'done',
            processedDevices: userProcessedDevices,
            totalDevices: devices.length,
            failedDevices: userFailedDevices,
          },
        }));
      };

      for (let i = 0; i < deadWearableCandidates.length; i += USERS_PER_BATCH) {
        const batch = deadWearableCandidates.slice(i, i + USERS_PER_BATCH);
        await Promise.all(
          batch.map((user, batchIndex) => processSingleUser(user, i + batchIndex))
        );
      }

      setRemoveDeadWearablesSummary({
        processedUsers,
        processedDevices,
        failedDevices,
      });
      await refreshData();
    } catch (err) {
      console.error('[REMOVE_DEAD_WEARABLES] Batch remove failed', err);
      alert(`Remove dead wearables failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRemoveDeadWearablesCurrentUser(null);
      setRemoveDeadWearablesRunning(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    if (!(date instanceof Date) || isNaN(date.getTime())) return 'Invalid Date';

    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return 'Invalid Date';
    }
  };

  const downloadCSV = () => {
    const headers = [
      '#',
      'Name',
      'Surname',
      'Email',
      'Username',
      'UID',
      'Role',
      'Subscription Type',
      'Subscription Via',
      'Day Posts',
      'Posts',
      'AI Chats',
      'Wearable Devices',
      'Training Logs',
      'Activity Logs',
      'Sessions',
      'Recently Logged (14d)',
      'Last Indexed',
    ];

    const rows = filteredUsers.map((user, index) => [
      (index + 1).toString(),
      user.name || '',
      user.surname || '',
      user.email || '',
      user.username || '',
      user.id || '',
      user.role || '',
      user.subscriptionType || 'default',
      user.subscriptionVia || 'default',
      user.dayPostsCount.toString(),
      user.postsCount.toString(),
      user.aiChatsCount.toString(),
      user.wearableDevicesCount.toString(),
      user.trainingLogsCount.toString(),
      user.activityLogsCount.toString(),
      user.sessionsCount.toString(),
      user.recentlyLogged === undefined ? 'undefined' : user.recentlyLogged ? 'true' : 'false',
      formatDate(user.lastIndexedAt),
    ]);

    const escapeCSVField = (field: string) => {
      const stringField = field ?? '';
      if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    const csvContent = [
      headers.map(escapeCSVField).join(','),
      ...rows.map((row) => row.map(escapeCSVField).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `user-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setFilter({});
  };

  const hasActiveFilters =
    filter.searchTerm ||
    filter.subscriptionType ||
    filter.subscriptionVia ||
    filter.hasWearable !== undefined ||
    filter.recentlyLogged !== undefined ||
    filter.minDayPosts !== undefined;

  if (loading && filteredUsers.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-base-content/70">Loading indexed user data...</p>
        </div>
      </div>
    );
  }

  // Show empty state with instructions when no data is indexed
  if (!loading && stats.totalUsers === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-semibold">User Analytics</h1>
            <span className="badge badge-primary badge-lg">Fast Load</span>
          </div>
        </div>
        
        <div className="card bg-base-100 shadow-lg border border-warning">
          <div className="card-body text-center py-12">
            <HiCog6Tooth className="w-16 h-16 mx-auto text-warning mb-4" />
            <h2 className="text-2xl font-bold mb-2">No Indexed Data Found</h2>
            <p className="text-base-content/70 mb-4 max-w-lg mx-auto">
              The User Analytics page uses pre-indexed data for fast loading. 
              You need to run the indexing process first to populate this data.
            </p>
            <div className="bg-base-200 rounded-lg p-4 mb-6 max-w-xl mx-auto text-left">
              <h3 className="font-semibold mb-2">Steps to get started:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-base-content/80">
                <li>Make sure the Firebase functions are deployed:
                  <code className="block ml-6 mt-1 bg-base-300 px-2 py-1 rounded text-xs">
                    cd functions && npm run build && firebase deploy --only functions
                  </code>
                </li>
                <li>Click the "Run Indexing" button below to index all user data</li>
                <li>Wait for the indexing to complete (may take a few minutes for many users)</li>
              </ol>
            </div>
            <button
              onClick={handleRunIndexing}
              disabled={indexingInProgress}
              className="btn btn-warning btn-lg text-white"
            >
              {indexingInProgress ? (
                <>
                  <span className="loading loading-spinner loading-sm mr-2"></span>
                  Indexing in Progress...
                </>
              ) : (
                <>
                  <HiCog6Tooth className="w-5 h-5 mr-2" />
                  Run Indexing Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-3xl font-semibold">User Analytics</h1>
          <span className="badge badge-primary badge-lg">Fast Load</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRunIndexing}
            disabled={indexingInProgress}
            className="btn btn-warning btn-sm text-white"
            title="Re-index all user data for faster loading"
          >
            {indexingInProgress ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                Indexing...
              </>
            ) : (
              <>
                <HiCog6Tooth className="w-4 h-4 mr-2" />
                Run Indexing
              </>
            )}
          </button>
          <button
            onClick={downloadCSV}
            disabled={filteredUsers.length === 0}
            className="btn btn-success btn-sm text-white"
            title={`Download ${filteredUsers.length} user(s) as CSV`}
          >
            <HiArrowDownTray className="w-4 h-4 mr-2" />
            Download CSV
          </button>
          <button
            onClick={refreshData}
            disabled={loading}
            className="btn btn-primary btn-sm"
          >
            <HiArrowPath className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={openRemoveDeadWearablesDialog}
            disabled={deadWearableCandidates.length === 0}
            className="btn btn-error btn-sm text-white"
            title={`Remove wearables for ${deadWearableCandidates.length} inactive default user(s)`}
          >
            Remove Dead Wearables
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <HiExclamationCircle className="w-5 h-5" />
          <span>{error.message}</span>
        </div>
      )}

      {/* Last Indexed Info */}
      {stats.lastIndexedAt && (
        <div className="alert alert-info mb-4">
          <HiClock className="w-5 h-5" />
          <span>Data last indexed: {formatDate(stats.lastIndexedAt)}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-primary">{stats.totalUsers}</div>
            <div className="text-sm text-base-content/70">Total Users</div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-warning">{stats.platinumUsers}</div>
            <div className="text-sm text-base-content/70">Platinum</div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-success">{stats.premiumUsers}</div>
            <div className="text-sm text-base-content/70">Premium</div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-info">{stats.coachUsers}</div>
            <div className="text-sm text-base-content/70">Coach</div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-secondary">{stats.plusUsers}</div>
            <div className="text-sm text-base-content/70">Plus</div>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-neutral">{stats.defaultUsers}</div>
            <div className="text-sm text-base-content/70">Default</div>
          </div>
        </div>
      </div>

      {/* Analytics Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-emerald-50 to-emerald-100 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiCalendarDays className="w-5 h-5 text-emerald-600" />
              <div className="text-sm text-base-content/70">Wellness Logs</div>
            </div>
            <div className="text-2xl font-bold text-emerald-700">{stats.totalDayPosts.toLocaleString()}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-blue-500/10 to-blue-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiChartBar className="w-5 h-5 text-blue-600" />
              <div className="text-sm text-base-content/70">Training Logs</div>
            </div>
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.totalTrainingLogs.toLocaleString()}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-orange-500/10 to-orange-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiChartBar className="w-5 h-5 text-orange-600" />
              <div className="text-sm text-base-content/70">Activity Logs</div>
            </div>
            <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">{stats.totalActivityLogs.toLocaleString()}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-cyan-500/10 to-cyan-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiSquares2X2 className="w-5 h-5 text-cyan-600" />
              <div className="text-sm text-base-content/70">Sessions</div>
            </div>
            <div className="text-2xl font-bold text-cyan-700 dark:text-cyan-400">{stats.totalSessions.toLocaleString()}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-purple-500/10 to-purple-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiCog6Tooth className="w-5 h-5 text-purple-600" />
              <div className="text-sm text-base-content/70">AI Chats</div>
            </div>
            <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.totalAiChats.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-pink-500/10 to-pink-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiEye className="w-5 h-5 text-pink-600" />
              <div className="text-sm text-base-content/70">Social Posts</div>
            </div>
            <div className="text-2xl font-bold text-pink-700 dark:text-pink-400">{stats.totalPosts.toLocaleString()}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-indigo-500/10 to-indigo-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiDevicePhoneMobile className="w-5 h-5 text-indigo-600" />
              <div className="text-sm text-base-content/70">Users with Wearables</div>
            </div>
            <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">{stats.usersWithWearables}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-amber-500/10 to-amber-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiChartBar className="w-5 h-5 text-amber-600" />
              <div className="text-sm text-base-content/70">Avg Wellness/User</div>
            </div>
            <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats.avgDayPostsPerUser}</div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-rose-500/10 to-rose-500/15 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center space-x-2">
              <HiUsers className="w-5 h-5 text-rose-600" />
              <div className="text-sm text-base-content/70">Filtered Results</div>
            </div>
            <div className="text-2xl font-bold text-rose-700 dark:text-rose-400">{filteredUsers.length}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users..."
              value={filter.searchTerm || ''}
              onChange={(e) => setFilter({ ...filter, searchTerm: e.target.value })}
              className="input input-bordered pl-10 w-full md:w-80"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline'}`}
          >
            <HiFunnel className="w-4 h-4 mr-1" />
            Filters
            {hasActiveFilters && <span className="badge badge-error badge-xs ml-1"></span>}
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn btn-sm btn-ghost text-error">
              <HiXMark className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="card bg-base-200 shadow-sm mb-6">
          <div className="card-body p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <SearchableSelect
                  options={[
                    { value: "platinum", label: "Platinum" },
                    { value: "premium", label: "Premium" },
                    { value: "coach", label: "Coach" },
                    { value: "plus", label: "Plus" },
                    { value: "default", label: "Default" },
                  ]}
                  value={filter.subscriptionType || 'all'}
                  onChange={(value) =>
                    setFilter({ ...filter, subscriptionType: value === 'all' ? undefined : value })
                  }
                  placeholder="Search..."
                  allOptionLabel="All"
                  emptyValue="all"
                  label="Subscription Type"
                  inputClassName="bg-base-200"
                />
              </div>
              <div>
                <SearchableSelect
                  options={[
                    { value: "stripe", label: "Stripe" },
                    { value: "admin", label: "Admin" },
                    { value: "trial", label: "Trial" },
                    { value: "coach", label: "Coach" },
                    { value: "default", label: "Default" },
                  ]}
                  value={filter.subscriptionVia || 'all'}
                  onChange={(value) =>
                    setFilter({ ...filter, subscriptionVia: value === 'all' ? undefined : value })
                  }
                  placeholder="Search..."
                  allOptionLabel="All"
                  emptyValue="all"
                  label="Subscription Via"
                  inputClassName="bg-base-200"
                />
              </div>
              <div>
                <SearchableSelect
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                  value={filter.hasWearable === undefined ? 'all' : filter.hasWearable ? 'yes' : 'no'}
                  onChange={(value) =>
                    setFilter({
                      ...filter,
                      hasWearable:
                        value === 'all' ? undefined : value === 'yes',
                    })
                  }
                  placeholder="Search..."
                  allOptionLabel="All"
                  emptyValue="all"
                  label="Has Wearable"
                  inputClassName="bg-base-200"
                />
              </div>
              <div>
                <label className="label text-sm font-medium">Min Day Posts</label>
                <input
                  type="number"
                  className="input input-bordered w-full input-sm"
                  placeholder="e.g. 10"
                  value={filter.minDayPosts ?? ''}
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      minDayPosts: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                />
              </div>
              <div>
                <label className="label text-sm font-medium">Recently Logged</label>
                <select
                  className="select select-bordered w-full select-sm"
                  value={filter.recentlyLogged === undefined ? '' : filter.recentlyLogged ? 'yes' : 'no'}
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      recentlyLogged:
                        e.target.value === '' ? undefined : e.target.value === 'yes',
                    })
                  }
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th className="font-semibold w-12">#</th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('name')}
                  >
                    User {renderSortIcon('name')}
                  </th>
                  <th className="font-semibold">UID</th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('email')}
                  >
                    Email {renderSortIcon('email')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('subscriptionType')}
                  >
                    Subscription {renderSortIcon('subscriptionType')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('dayPostsCount')}
                  >
                    Day Posts {renderSortIcon('dayPostsCount')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('postsCount')}
                  >
                    Posts {renderSortIcon('postsCount')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('aiChatsCount')}
                  >
                    AI Chats {renderSortIcon('aiChatsCount')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('wearableDevicesCount')}
                  >
                    Wearables {renderSortIcon('wearableDevicesCount')}
                  </th>
                  <th
                    className="font-semibold cursor-pointer hover:bg-base-200"
                    onClick={() => handleSort('sessionsCount')}
                  >
                    Sessions {renderSortIcon('sessionsCount')}
                  </th>
                  <th className="font-semibold">Recently Logged</th>
                  <th className="font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-8 text-base-content/60">
                      {filter.searchTerm || hasActiveFilters
                        ? 'No users found matching your filters.'
                        : 'No indexed users found. Click "Run Indexing" to index user data.'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr key={user.id} className="hover:bg-base-200">
                      <td className="text-base-content/60 font-medium">{index + 1}</td>
                      <td>
                        <div className="flex items-center space-x-3">
                          {user.imageUrlTiny || user.imageUrl ? (
                            <img
                              src={user.imageUrlTiny || user.imageUrl}
                              alt={`${user.name}'s avatar`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {user.name} {user.surname}
                            </div>
                            <div className="text-sm text-base-content/60">@{user.username || 'no-username'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="text-xs bg-base-200 px-2 py-1 rounded">
                          {user.id.substring(0, 12)}...
                        </code>
                      </td>
                      <td className="text-sm">{user.email}</td>
                      <td>
                        <div className="flex flex-col space-y-1">
                          <span
                            className={`badge ${
                              user.subscriptionType === 'platinum'
                                ? 'badge-warning'
                                : user.subscriptionType === 'premium'
                                ? 'badge-success'
                                : user.subscriptionType === 'coach'
                                ? 'badge-info'
                                : user.subscriptionType === 'plus'
                                ? 'badge-secondary'
                                : 'badge-neutral'
                            } badge-sm`}
                          >
                            {user.subscriptionType || 'default'}
                          </span>
                          <span className="text-xs text-base-content/60">via {user.subscriptionVia || 'default'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="font-medium">{user.dayPostsCount}</span>
                      </td>
                      <td>
                        <span className={`font-medium ${user.postsCount > 0 ? 'text-info' : 'text-base-content/50'}`}>
                          {user.postsCount}
                        </span>
                      </td>
                      <td>
                        <span className={`font-medium ${user.aiChatsCount > 0 ? 'text-purple-600' : 'text-base-content/50'}`}>
                          {user.aiChatsCount}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`font-medium ${
                            user.wearableDevicesCount > 0 ? 'text-success' : 'text-base-content/50'
                          }`}
                        >
                          {user.wearableDevicesCount}
                        </span>
                      </td>
                      <td>
                        <span className="font-medium">{user.sessionsCount}</span>
                      </td>
                      <td>
                        {user.recentlyLogged === undefined ? (
                          <span className="badge badge-sm badge-warning">Undefined</span>
                        ) : (
                          <span className={`badge badge-sm ${user.recentlyLogged ? 'badge-success' : 'badge-ghost'}`}>
                            {user.recentlyLogged ? 'True' : 'False'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleNotificationClick(user)}
                            className="btn btn-sm btn-ghost text-primary hover:bg-primary hover:text-white"
                            title={`Send push notification to ${user.name}`}
                          >
                            <HiBell className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEmailClick(user)}
                            className="btn btn-sm btn-ghost text-secondary hover:bg-secondary hover:text-white"
                            title={`Send email to ${user.name}`}
                          >
                            <HiEnvelope className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(`/feed/?uid=${user.id}`, '_blank')}
                            className="btn btn-sm btn-ghost text-primary hover:bg-primary hover:text-white"
                            title={`View ${user.name}'s feed`}
                          >
                            <HiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(`/dashboard/?uid=${user.id}`, '_blank')}
                            className="btn btn-sm btn-ghost text-info hover:bg-info hover:text-white"
                            title={`View ${user.name}'s dashboard`}
                          >
                            <HiSquares2X2 className="w-4 h-4" />
                          </button>
                          {user.wearableDevicesCount > 0 && (
                            <button
                              onClick={() => handleWearableClick(user)}
                              className="btn btn-sm btn-ghost text-info hover:bg-info hover:text-white"
                              title={`View ${user.name}'s wearable devices (${user.wearableDevicesCount})`}
                            >
                              <HiDevicePhoneMobile className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Notification Dialog */}
      {notificationDialog.targetUser && (
        <SendNotificationDialog
          isOpen={notificationDialog.isOpen}
          onClose={() => setNotificationDialog({ isOpen: false, targetUser: null })}
          targetUser={notificationDialog.targetUser}
        />
      )}

      {/* Email Dialog */}
      {emailDialog.targetUser && (
        <SendEmailDialog
          isOpen={emailDialog.isOpen}
          onClose={() => setEmailDialog({ isOpen: false, targetUser: null })}
          targetUser={emailDialog.targetUser}
        />
      )}

      {/* Wearable Details Dialog */}
      {wearableDialog.targetUser && (
        <WearableDetailsDialog
          isOpen={wearableDialog.isOpen}
          onClose={() => setWearableDialog({ isOpen: false, targetUser: null, devices: [] })}
          user={wearableDialog.targetUser}
        />
      )}

      {/* Remove Dead Wearables Dialog */}
      {removeDeadWearablesDialogOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="fixed inset-0 bg-black bg-opacity-40"
            onClick={() => {
              if (!removeDeadWearablesRunning) setRemoveDeadWearablesDialogOpen(false);
            }}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden border border-base-300">
              <div className="p-4 border-b border-base-300 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Remove Dead Wearables</h3>
                  <p className="text-sm text-base-content/70">
                    Criteria: Recently Logged = false, Subscription = default, Has wearables.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="btn btn-error btn-sm text-white"
                    onClick={handleRemoveDeadWearables}
                    disabled={removeDeadWearablesRunning || deadWearableCandidates.length === 0}
                  >
                    {removeDeadWearablesRunning ? (
                      <>
                        <span className="loading loading-spinner loading-xs mr-2"></span>
                        Removing...
                      </>
                    ) : (
                      'Remove'
                    )}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setRemoveDeadWearablesDialogOpen(false)}
                    disabled={removeDeadWearablesRunning}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="p-4 border-b border-base-300 bg-base-200/50">
                <div className="text-sm">
                  <span className="font-medium">{deadWearableCandidates.length}</span> eligible users
                </div>
                {removeDeadWearablesCurrentUser && (
                  <div className="text-sm mt-2">
                    Currently processing{' '}
                    <span className="font-medium">{removeDeadWearablesCurrentUser.name}</span>{' '}
                    ({removeDeadWearablesCurrentUser.index}/{removeDeadWearablesCurrentUser.total})
                  </div>
                )}
                {removeDeadWearablesSummary && (
                  <div className="text-sm mt-2">
                    Completed: {removeDeadWearablesSummary.processedUsers} users,{' '}
                    {removeDeadWearablesSummary.processedDevices} devices,{' '}
                    {removeDeadWearablesSummary.failedDevices} failed.
                  </div>
                )}
              </div>

              <div className="overflow-auto max-h-[52vh]">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Subscription</th>
                      <th>Recently Logged</th>
                      <th>UID</th>
                      <th>Wearables</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadWearableCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-base-content/60">
                          No eligible users found.
                        </td>
                      </tr>
                    ) : (
                      deadWearableCandidates.map((user) => {
                        const status = removeDeadWearablesStatusByUser[user.id];
                        return (
                          <tr key={user.id}>
                            <td className="font-medium">{user.name} {user.surname}</td>
                            <td className="text-sm">{user.email}</td>
                            <td>
                              <span className="badge badge-sm badge-neutral">
                                {user.subscriptionType || 'default'}
                              </span>
                            </td>
                            <td>
                              {user.recentlyLogged === undefined ? (
                                <span className="badge badge-sm badge-warning">Undefined</span>
                              ) : (
                                <span className={`badge badge-sm ${user.recentlyLogged ? 'badge-success' : 'badge-ghost'}`}>
                                  {user.recentlyLogged ? 'True' : 'False'}
                                </span>
                              )}
                            </td>
                            <td>
                              <code className="text-xs bg-base-200 px-2 py-1 rounded">
                                {user.id.substring(0, 12)}...
                              </code>
                            </td>
                            <td>{user.wearableDevices.length}</td>
                            <td>
                              {status?.status === 'in_progress' && (
                                <span className="text-info">
                                  In progress ({status.processedDevices}/{status.totalDevices})
                                </span>
                              )}
                              {status?.status === 'done' && (
                                <span className={status.failedDevices > 0 ? 'text-warning' : 'text-success'}>
                                  Done ({status.processedDevices}/{status.totalDevices})
                                  {status.failedDevices > 0 ? `, ${status.failedDevices} failed` : ''}
                                </span>
                              )}
                              {(!status || status.status === 'pending') && (
                                <span className="text-base-content/60">Pending</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAnalytics;

